import type { RosterAgent, SidebarSettings } from "@ark-bot/protocol";

import { orderRoster, previewOf } from "./roster.js";
import { arrange } from "./sidebar-settings.js";

/**
 * Getting around without the mouse, and the roster's own view choices on this
 * device: the switcher's order and keys, the sidebar put away, the sections
 * folded, and the activity view. None of it is host settings; it is how this
 * screen is used, like the panes' widths (panes.ts).
 */

/** The keys a shortcut is read from; a KeyboardEvent has them all. */
export interface Keys {
  readonly key: string;
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly altKey: boolean;
  readonly shiftKey: boolean;
  readonly repeat?: boolean;
}

/** ⌘ on a Mac or an iPad, Ctrl elsewhere: the one modifier the app's shortcuts use. */
function command(keys: Keys): boolean {
  return (keys.metaKey || keys.ctrlKey) && !keys.altKey && !keys.shiftKey && keys.repeat !== true;
}

/** ⌘K or Ctrl+K: open or close the switcher. */
export function isSwitcherKey(keys: Keys): boolean {
  return command(keys) && keys.key.toLowerCase() === "k";
}

/** ⌘B or Ctrl+B: put the sidebar away or bring it back, as an editor's does. */
export function isSidebarKey(keys: Keys): boolean {
  return command(keys) && keys.key.toLowerCase() === "b";
}

/** ⌘1 to ⌘9 in the switcher: the index of the bot it opens, or null for any other key. */
export function digitShortcut(keys: Keys): number | null {
  if (!command(keys) || !/^[1-9]$/.test(keys.key)) return null;
  return Number(keys.key) - 1;
}

/** Whether this platform names the modifier ⌘; everywhere else it is Ctrl. */
export function isApple(platform: string = globalThis.navigator?.platform ?? globalThis.navigator?.userAgent ?? ""): boolean {
  return /Mac|iPhone|iPad|iPod/i.test(platform);
}

/**
 * The bots in the order the sidebar shows them, pinned first, then each
 * section, then the rest; hidden ones last, since the switcher is also the
 * way to reach them. So ⌘1 is the bot at the top of the sidebar.
 */
export function switcherOrder(agents: readonly RosterAgent[], sidebar: SidebarSettings): RosterAgent[] {
  const arranged = arrange(agents, sidebar);
  return [...arranged.pinned, ...arranged.sections.flatMap((section) => section.agents), ...arranged.unassigned, ...arranged.hidden];
}

/** Whether a bot answers what was typed in the switcher: its name, its role, or what it said last. */
export function matchesSwitcher(agent: RosterAgent, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  const haystack = [agent.name, agent.config.title ?? "", agent.config.description ?? "", previewOf(agent)?.text ?? ""].join("\n").toLowerCase();
  return haystack.includes(needle);
}

/** What a bot is up to, as the activity view sorts it: waiting on the user first, then at work, then with something unread. */
export type Activity = "needs-you" | "working" | "unread";

const ACTIVITY_RANK: Record<Activity, number> = { "needs-you": 0, working: 1, unread: 2 };

export function activityOf(agent: RosterAgent, unread: boolean): Activity | null {
  if (previewOf(agent)?.attention === true) return "needs-you";
  if (agent.busy) return "working";
  if (unread) return "unread";
  return null;
}

/** The activity view: only the bots with something going on, the ones waiting on the user at the top, each group newest first. */
export function activityList(agents: readonly RosterAgent[], unreadOf: (agent: RosterAgent) => boolean): RosterAgent[] {
  const ranked = orderRoster(agents).flatMap((agent) => {
    const activity = activityOf(agent, unreadOf(agent));
    return activity == null ? [] : [{ agent, rank: ACTIVITY_RANK[activity] }];
  });
  return ranked.sort((a, b) => a.rank - b.rank).map((one) => one.agent);
}

/** The folded sections, as stored: ids, and the unassigned block as "". Anything unreadable is nothing folded. */
export function loadFolded(stored: string | null): Set<string> {
  if (stored == null) return new Set();
  try {
    const parsed = JSON.parse(stored) as unknown;
    return new Set(Array.isArray(parsed) ? parsed.filter((one): one is string => typeof one === "string") : []);
  } catch {
    return new Set();
  }
}

/** Folds a section that was open, opens one that was folded. */
export function toggleFolded(folded: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(folded);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/**
 * Where a pin moves for Alt+← or Alt+→ on the rail: the neighbour it swaps
 * past and on which side, or null at the rail's end.
 */
export function pinStep(pinned: readonly string[], id: string, direction: -1 | 1): { readonly targetId: string; readonly position: "before" | "after" } | null {
  const index = pinned.indexOf(id);
  const target = pinned[index + direction];
  if (index < 0 || target == null) return null;
  return { targetId: target, position: direction < 0 ? "before" : "after" };
}

/** A shortcut as this platform writes it: ⌘K on a Mac, Ctrl+K elsewhere. */
export function shortcutLabel(key: string, apple: boolean = isApple()): string {
  return apple ? `⌘${key}` : `Ctrl+${key}`;
}
